import { guildNameSchema } from '@discordo/shared';
import { useState, type FormEvent, type ReactElement } from 'react';

import { ApiError } from '../lib/api';
import type { GuildSummary } from '../store/channels';

/**
 * Leftmost column: one circular icon per server, and a `+` at the end.
 *
 * This is the only component that knows there is more than one server, and
 * even here that knowledge is a list, never a branch: every entry is rendered
 * by the same code from the same shape, and nothing about a particular server
 * is special-cased. What differs between two servers — the name, the icon — is
 * data read at runtime (§3 of the Fase 4 design).
 *
 * A server with no image shows the initials of its name, so a bar of many
 * servers is still readable without a single upload.
 */

const GENERIC_FAILURE = 'Não foi possível criar o servidor. Tente de novo.';

/**
 * Up to two initials: the first letter of the first and last words. One word
 * gives one letter, which is what makes a bar of short names legible instead
 * of a column of identical blobs.
 */
export function guildInitials(name: string): string {
  const words = name.trim().split(/\s+/u).filter((word) => word !== '');
  const first = words[0];

  if (first === undefined) {
    return '?';
  }

  const last = words.length > 1 ? words[words.length - 1] : undefined;
  const initials = last === undefined ? first.slice(0, 1) : `${first.slice(0, 1)}${last.slice(0, 1)}`;

  return initials.toUpperCase();
}

// ---------------------------------------------------------------------------
// Creation dialog
// ---------------------------------------------------------------------------

interface CreateGuildDialogProps {
  onCreate: (name: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Asks for the name and nothing else.
 *
 * The name is validated with `guildNameSchema` — the very schema the API parses
 * the body with — so the rule and its wording live in one place and a name that
 * could never be accepted never leaves the browser. Everything else about the
 * new server (its `#geral` and its `Geral`) is created by the API, by the same
 * function the seed uses.
 */
function CreateGuildDialog({ onCreate, onClose }: CreateGuildDialogProps): ReactElement {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = guildNameSchema.safeParse(name);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }

    setSubmitting(true);
    try {
      await onCreate(parsed.data);
      onClose();
    } catch (cause) {
      // The typed name stays in the field: a refusal — the ceiling of servers,
      // above all — must never cost someone what they wrote.
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
      setSubmitting(false);
    }
  }

  return (
    <div className="guild-dialog-backdrop">
      <form
        className="guild-dialog"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <h2 className="guild-dialog-title">Criar servidor</h2>
        <p className="guild-dialog-hint">
          Ele nasce com um canal de texto e um de voz. Você pode renomear depois.
        </p>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        <div className="auth-field">
          <label htmlFor="guild-name">Nome do servidor</label>
          <input
            id="guild-name"
            name="name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="guild-dialog-actions">
          <button type="button" className="guild-dialog-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------

export interface GuildBarProps {
  guilds: GuildSummary[];
  /** The server being read; `null` while the first list is still loading. */
  activeGuildId: string | null;
  onSelect: (guildId: string) => void;
  onCreate: (name: string) => Promise<void>;
}

export function GuildBar({
  guilds,
  activeGuildId,
  onSelect,
  onCreate,
}: GuildBarProps): ReactElement {
  const [creating, setCreating] = useState(false);

  return (
    <nav className="guild-bar" aria-label="Servidores">
      {/*
        A marca no topo da coluna, acima dos servidores. Decorativa por isso
        mesmo: quem navega por leitor de tela já tem o `aria-label` do `nav`
        dizendo o que a coluna é, e anunciar o logo de novo só repetiria.
      */}
      <img className="guild-bar-brand" src="/brand/icone-contorno.png" alt="" aria-hidden="true" />

      <ul className="guild-bar-list">
        {guilds.map((guild) => {
          const active = guild.id === activeGuildId;
          return (
            <li key={guild.id}>
              <button
                type="button"
                className={active ? 'guild-icon is-active' : 'guild-icon'}
                // `aria-current` is only meaningful on the open server; the
                // others carry no attribute at all.
                {...(active ? { 'aria-current': 'true' as const } : {})}
                aria-label={guild.name}
                title={guild.name}
                onClick={() => onSelect(guild.id)}
              >
                {guild.iconUrl === null ? (
                  guildInitials(guild.name)
                ) : (
                  <img className="guild-icon-image" src={guild.iconUrl} alt={guild.name} />
                )}
              </button>
            </li>
          );
        })}

        <li>
          <button
            type="button"
            className="guild-icon guild-icon-add"
            aria-label="Criar servidor"
            title="Criar servidor"
            onClick={() => setCreating(true)}
          >
            +
          </button>
        </li>
      </ul>

      {creating && (
        <CreateGuildDialog onCreate={onCreate} onClose={() => setCreating(false)} />
      )}
    </nav>
  );
}

export default GuildBar;
